import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api/client';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

interface MenuVariant {
    id: string;
    name: string;
    price?: number;
    available?: boolean;
}

interface MenuItem {
    id: string;
    name: string;
    description?: string;
    price?: number;
    categoryId: string;
    subcategoryId?: string;
    variants?: MenuVariant[];
}

interface Category {
    id: string;
    name: string;
    description?: string;
}

interface Subcategory {
    id: string;
    name: string;
    description?: string;
    categoryId: string;
}

interface TenantContextType {
    menuData: MenuItem[];
    categories: Category[];
    subcategories: Subcategory[];
    loading: boolean;
    error: string | null;
    reloadData: () => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [menuData, setMenuData] = useState<MenuItem[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { tenantId: authTenantId, isAuthenticated } = useAuth();

    const loadData = useCallback(async () => {
        const storedTenantId = authTenantId || localStorage.getItem('tenantId') || '';
        const normalizedTenantId = storedTenantId.trim();

        if (!isAuthenticated || normalizedTenantId === '') {
            setLoading(false);
            setError(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Prova prima l'endpoint snapshot che restituisce tutto
            const snapshotResponse = await apiFetch('/api/menu/snapshot', {
                headers: {
                    'X-Tenant-Id': normalizedTenantId
                }
            });
            if (snapshotResponse.ok) {
                const snapshotData = await snapshotResponse.json();
                logger.debug('Snapshot loaded');
                if (snapshotData.categories) {
                    setCategories(snapshotData.categories);
                }
                if (snapshotData.subcategories) {
                    setSubcategories(snapshotData.subcategories.map((sub: any) => ({
                        id: String(sub.id),
                        name: sub.name,
                        description: sub.description,
                        categoryId: String(sub.categoryId)
                    })));
                }
                // I prodotti sono sotto 'products', non 'menuItems'
                if (snapshotData.products) {
                    setMenuData(snapshotData.products.map((prod: any) => ({
                        id: String(prod.id),
                        name: prod.name,
                        description: prod.description,
                        price: prod.price,
                        categoryId: String(prod.categoryId),
                        subcategoryId: prod.subcategoryId != null ? String(prod.subcategoryId) : undefined,
                        variants: Array.isArray(prod.variants)
                            ? prod.variants.map((variant: any) => ({
                                id: String(variant.id ?? `${prod.id}-var-${variant.position ?? 0}`),
                                name: variant.name,
                                price: variant.price,
                                available: variant.available !== false
                            }))
                            : []
                    })));
                } else if (snapshotData.menuItems) {
                    setMenuData(snapshotData.menuItems.map((prod: any) => ({
                        id: String(prod.id),
                        name: prod.name,
                        description: prod.description,
                        price: prod.price,
                        categoryId: String(prod.categoryId),
                        subcategoryId: prod.subcategoryId != null ? String(prod.subcategoryId) : undefined,
                        variants: Array.isArray(prod.variants)
                            ? prod.variants.map((variant: any) => ({
                                id: String(variant.id ?? `${prod.id}-var-${variant.position ?? 0}`),
                                name: variant.name,
                                price: variant.price,
                                available: variant.available !== false
                            }))
                            : []
                    })));
                }
                return;
            }

            // Fallback: carica separatamente categorie e menu items
            const [categoriesResponse, menuResponse] = await Promise.all([
                apiFetch('/api/category', {
                    headers: {
                        'X-Tenant-Id': normalizedTenantId
                    }
                }),
                apiFetch('/api/menuitem', {
                    headers: {
                        'X-Tenant-Id': normalizedTenantId
                    }
                })
            ]);

            if (categoriesResponse.ok) {
                const categoriesData = await categoriesResponse.json();
                setCategories(categoriesData);
            }

            if (menuResponse.ok) {
                const menuItemsData = await menuResponse.json();
                setMenuData(menuItemsData.map((prod: any) => ({
                    id: String(prod.id),
                    name: prod.name,
                    description: prod.description,
                    price: prod.price,
                    categoryId: String(prod.categoryId),
                    subcategoryId: prod.subcategoryId != null ? String(prod.subcategoryId) : undefined,
                    variants: Array.isArray(prod.variants)
                        ? prod.variants.map((variant: any) => ({
                            id: String(variant.id ?? `${prod.id}-var-${variant.position ?? 0}`),
                            name: variant.name,
                            price: variant.price,
                            available: variant.available !== false
                        }))
                        : []
                })));
            }

            const subcategoryResponse = await apiFetch('/api/subcategories', {
                headers: {
                    'X-Tenant-Id': normalizedTenantId
                }
            });
            if (subcategoryResponse.ok) {
                const subsData = await subcategoryResponse.json();
                setSubcategories(subsData.map((sub: any) => ({
                    id: String(sub.id),
                    name: sub.name,
                    description: sub.description,
                    categoryId: String(sub.category?.id ?? sub.categoryId ?? sub.category_id ?? '')
                })).filter((sub: Subcategory) => sub.categoryId !== ''));
            }

        } catch (err) {
            console.error('Errore nel caricamento dati:', err);
            setError('Errore nel caricamento dei dati del menu');

            // Fallback ai dati mock in caso di errore
            setCategories([
                { id: 'pizza', name: 'Pizza' },
                { id: 'primi', name: 'Primi Piatti' },
                { id: 'secondi', name: 'Secondi Piatti' },
                { id: 'dolci', name: 'Dolci' }
            ]);
            setSubcategories([
                { id: 'pizza-classiche', name: 'Classiche', description: '', categoryId: 'pizza' }
            ]);
            setMenuData([
                {
                    id: '1',
                    name: 'Margherita',
                    description: 'Pizza con pomodoro, mozzarella e basilico',
                    price: 8.50,
                    categoryId: 'pizza',
                    subcategoryId: 'pizza-classiche',
                    variants: []
                },
                {
                    id: '2',
                    name: 'Carbonara',
                    description: 'Pasta con uova, pancetta e parmigiano',
                    price: 12.00,
                    categoryId: 'primi',
                    variants: []
                },
                {
                    id: '3',
                    name: 'Bistecca alla griglia',
                    description: 'Carne di manzo grigliata con contorno di verdure',
                    price: 18.00,
                    categoryId: 'secondi',
                    variants: []
                }
            ]);
        } finally {
            setLoading(false);
        }
    }, [authTenantId, isAuthenticated]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const reloadData = useCallback(() => {
        loadData();
    }, [loadData]);

    const value: TenantContextType = {
        menuData,
        categories,
        subcategories,
        loading,
        error,
        reloadData
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};